import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, Activity, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaceBranding } from "../contexts/WorkspaceBrandingContext";
import api from "../lib/api";

function relativeTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
}

export default function Dashboard() {
    const { user } = useAuth();
    const { businessName } = useWorkspaceBranding();
    const [activity, setActivity] = useState([]);
    const [loadingActivity, setLoadingActivity] = useState(true);
    const [profileIncomplete, setProfileIncomplete] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [actRes, profileRes] = await Promise.all([
                    api.get("/activity?limit=6"),
                    api.get("/business-profile").catch(() => null),
                ]);
                if (!mounted) return;
                setActivity(actRes.data || []);
                if (profileRes?.data) {
                    const p = profileRes.data;
                    const isEmpty =
                        !(p.phone || "").trim() &&
                        !(p.cta_text || "").trim() &&
                        !(p.service_areas || []).length &&
                        !(p.service_descriptions || []).length;
                    setProfileIncomplete(isEmpty);
                }
            } catch (_err) {
                if (mounted) setActivity([]);
            } finally {
                if (mounted) setLoadingActivity(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <section
            className="mx-auto w-full max-w-5xl px-5 sm:px-8 pt-16 sm:pt-24 pb-40 md:pb-24"
            data-testid="dashboard-page"
        >
            {/* Headline */}
            <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                className="text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-50 text-balance"
                data-testid="dashboard-welcome-heading"
            >
                Your business command center.
            </motion.h1>

            <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08 }}
                className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-zinc-500"
            >
                Everything connected to your website, organized in one clean workspace.
            </motion.p>

            {/* Ambient last-update line */}
            {!loadingActivity && activity.length > 0 && (
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mt-3 text-[13px] text-zinc-500"
                    data-testid="dashboard-last-update"
                >
                    Last update:{" "}
                    <span className="text-zinc-400">{activity[0].message}</span>{" "}
                    · {relativeTime(activity[0].created_at)}
                </motion.p>
            )}

            {/* Onboarding banner — shown when BusinessProfile is empty */}
            {user?.role === "client" && profileIncomplete && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.16 }}
                    className="mt-10 rounded-xl border border-zinc-800 bg-zinc-950/40 p-6 sm:p-7"
                    data-testid="dashboard-onboarding-banner"
                >
                    <div className="flex items-start gap-4">
                        <div className="h-9 w-9 shrink-0 rounded-md border border-zinc-800 flex items-center justify-center">
                            <Sparkles className="h-4 w-4 text-zinc-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[15px] font-medium text-zinc-50 tracking-tight">
                                Welcome to WebJesters.
                            </p>
                            <p className="mt-1.5 text-[13.5px] text-zinc-400 leading-relaxed max-w-lg">
                                Please complete your profile so we can deploy your website. Add your phone, hours, and a quick call-to-action — it takes about two minutes.
                            </p>
                            <Link
                                to="/settings"
                                data-testid="dashboard-onboarding-cta"
                                className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-zinc-50 text-zinc-950 text-[13px] font-medium hover:bg-white transition-colors"
                            >
                                Complete profile
                                <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Identity cards */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.18 }}
                className="mt-10 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
                <div
                    className="rounded-lg border border-zinc-800 bg-transparent p-5 sm:p-8"
                    data-testid="dashboard-card-tenant"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md border border-zinc-800 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-zinc-400 stroke-[1.75]" />
                        </div>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                            Business
                        </p>
                    </div>
                    <p
                        className="mt-6 text-[20px] font-medium tracking-tight text-zinc-50"
                        data-testid="dashboard-business-name"
                    >
                        {businessName}
                    </p>
                    <p className="mt-2 text-[12.5px] text-zinc-500">
                        {user?.tenant?.created_at
                            ? `With WebJesters since ${new Date(user.tenant.created_at).toLocaleDateString(
                                  undefined,
                                  { year: "numeric", month: "short" }
                              )}`
                            : "—"}
                    </p>
                </div>

                <div
                    className="rounded-lg border border-zinc-800 bg-transparent p-5 sm:p-8"
                    data-testid="dashboard-card-role"
                >
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-md border border-zinc-800 flex items-center justify-center">
                            <ShieldCheck className="h-4 w-4 text-zinc-400 stroke-[1.75]" />
                        </div>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                            Signed in
                        </p>
                    </div>
                    <p
                        className="mt-6 text-[20px] font-medium tracking-tight text-zinc-50 capitalize"
                        data-testid="dashboard-role"
                    >
                        {user?.role || "—"}
                    </p>
                    <p className="mt-2 text-[12.5px] text-zinc-500 truncate">
                        {user?.email}
                    </p>
                </div>
            </motion.div>

            {/* Activity feed */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.32 }}
                className="mt-10 sm:mt-16"
                data-testid="dashboard-activity"
            >
                <div className="flex items-center gap-2 mb-4">
                    <Activity className="h-3.5 w-3.5 text-zinc-500 stroke-[1.75]" />
                    <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                        Recent activity
                    </p>
                </div>

                {loadingActivity ? (
                    <p className="text-[13.5px] text-zinc-500">Loading…</p>
                ) : activity.length === 0 ? (
                    <p
                        className="max-w-xl text-[15px] leading-relaxed text-zinc-400"
                        data-testid="dashboard-activity-empty"
                    >
                        Nothing here yet. Updates you make to your business data will show up here.
                    </p>
                ) : (
                    <ul className="space-y-3 max-w-2xl" data-testid="dashboard-activity-list">
                        {activity.map((e) => (
                            <li
                                key={e.id}
                                className="flex items-start justify-between gap-4 py-2.5 border-b border-zinc-900 last:border-0"
                            >
                                <div className="min-w-0">
                                    <p className="text-[14px] text-zinc-200">{e.message}</p>
                                    <p className="mt-0.5 text-[12px] text-zinc-500 truncate">
                                        {e.actor_email}
                                    </p>
                                </div>
                                <span className="shrink-0 text-[12px] text-zinc-500 tabular-nums">
                                    {relativeTime(e.created_at)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </motion.div>
        </section>
    );
}
